import React, { useEffect, useState } from 'react'
import { ColDef, ColumnApi, GridApi, ICellRendererParams, ValueGetterParams } from "ag-grid-community";
import * as selector from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import ButtonComponent from '../shared/button/button.component';
import { ActivateIcon, BackMobileIcon, EditIcon, PaymentCopyLinkIcon, PlusWhiteIcon, RevokeIcon } from '../../icon';
import { AppRoutes } from '../routes/route.path';
import { useNavigate } from 'react-router-dom';
import APIAccessViewComponent from './api-access-view.component';
import { ApiAccessService } from '../../services/apiAccessService';
import { ApiAccessEnum, ApiAccessModel, mapToApiAccessModel } from '../../models/ApiAccess.model';
import { UtilityService } from '../../services/UtilityService';
import { dateComparator } from '../shared/grid/grid.services';
import GridComponent from '../shared/grid';
import { ActionLabels, appNote, emptyLabels, GridStatusLabelVariant, ToasterTexts } from '../shared/constants';
import { ActionsRenderer } from '../shared/grid/actionsRenderer';
import { StatusRenderer } from '../shared/grid/statusRenderer';
import setToast from '../../helpers/toaster.helper';
import { ToasterType } from '../shared/toaster/toaster.model';
import Confirm_Dialog from '../shared/confirm-dialog.component';

const APIACCESSCOMPONENT = () => {
    const navigate = useNavigate();
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice)

    const [showCreateApiAccessDialog, setCreateApiAccessDialog] = useState<boolean>(false)
    const [data, setData] = useState<ApiAccessModel[]>()
    const [gridApi, setGridApi] = useState<GridApi>(null);
    const [columnApi, setColumnApi] = useState<ColumnApi>(null);
    const [selectedRow, setSelectedRow] = useState<ApiAccessModel>()
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false)
    const [text, setText] = useState<string>("")
    const [buttonName, setButtonName] = useState<string>("")
    const [isLoading, setIsloading] = useState<boolean>(false)

    const ColumnDefs: ColDef[] = [
        {
            field: "Name",
            headerName: "Name",
            valueGetter: (params: ValueGetterParams) => {
                return !!params.data.Name ? params.data.Name : "--"
            }
        },
        {
            field: "Id",
            headerName: "Key ID",
            valueGetter: (params: ValueGetterParams) => {
                return !!params.data.Id ? params.data.Id : "--"
            },
            cellRenderer: (params: any) => {
                const handleCopy = () => {
                    navigator.clipboard.writeText(params?.data?.Id);
                    setToast(ToasterTexts.LINK_HAS_BEEN_COPIED?.replace("Link", "Key"), ToasterType.SUCCESS)
                };

                return (
                    <div className="d-flex align-center">
                        <span className='mr-1 w-space o-hidden t-overflow-ellipsis' >{!!params.data.Id ? params.data.Id : "--"}</span>
                        <PaymentCopyLinkIcon onClick={() => handleCopy()} className='h-4 w-4 c-pointer' />
                    </div>
                );
            },
            cellStyle: { textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", paddingTop: "15px", }
        },
        {
            field: "Status",
            headerName: "Status",
            valueGetter: (params: ValueGetterParams) => {
                return params.data.Status === ApiAccessEnum.Active ? "Active" : "Revoked"
            },
            cellRendererSelector: (params: ICellRendererParams) => {
                if (params.data) {
                    const label = params.data.Status === ApiAccessEnum.Active ? "Active" : "Revoked";
                    const variant = params.data.Status === ApiAccessEnum.Active ? GridStatusLabelVariant.Active : GridStatusLabelVariant.Inactive;
                    const Status = {
                        component: StatusRenderer,
                        params: {
                            Variant: variant, Label: label, Id: params.data.ID
                        }
                    }
                    return Status
                }
                return undefined
            },
        },
        {
            field: "PhoneNumber",
            headerName: "Phone Number",
            valueFormatter: (params) => {
                return params.data?.FromPhonenumbers.join(", ")
            },
            valueGetter: (params: ValueGetterParams) => {
                const raw =
                    params.data?.PhoneNumber ||
                    params.data?.MaskedPhoneNumber ||
                    "";
                return params.data?.FromPhonenumbers.join(", ");
            },
            cellStyle: { textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", paddingTop: "15px", }
        },
        {
            field: "updatedDateTime",
            headerName: "Last Used",
            valueGetter: (params: ValueGetterParams) => UtilityService.convertDateToDay(params.data.UpdatedDateTime ?? params.data.CreatedDateTime).toString(),
            hide: IsMobileDevice,
            suppressSizeToFit: true
        },
        {
            field: "CreatedDateTime",
            headerName: "Created Date",
            valueGetter: (params: ValueGetterParams) => UtilityService.convertDateToDay(params.data.CreatedDateTime).toString(),
            sort: !IsMobileDevice ? 'desc' : null,
            hide: IsMobileDevice,
            comparator: (valueA, valueB, nodeA, nodeB) => dateComparator(nodeA.data.CreatedDateTime, nodeB.data.CreatedDateTime),
            minWidth: 150,
            width: 150,
            maxWidth: 150,
            suppressSizeToFit: true
        },
        {
            field: "Action",
            maxWidth: 40,
            headerName: "",
            pinned: "right",
            lockPinned: true,
            lockPosition: "right",
            cellClass: "ag-cell-actions-style",
            headerClass: "bg-color-secondary",
            cellRenderer: params => <ActionsRenderer MenuData={getMenuActions(params.data)}
                {...params}
            />,
            resizable: false,
            sortable: false,
            hide: IsMobileDevice
        },
    ]
    const getMenuActions = (data: ApiAccessModel) => {
        return [{
            Name: ActionLabels.EDIT,
            Icon: EditIcon,
            OnClick: () => { setSelectedRow(data); openCreateAPIAccessDialog() },
        },
        {
            Name: "Revoke",
            Icon: RevokeIcon,
            OnClick: () => { openRevokeDialog(data) },
        },
        {
            Name: "Activate Webhook",
            Icon: ActivateIcon,
            OnClick: () => { openActivateWebhookDialog(data) },
            Hide: !data?.WebhookDisabled
        }]
    }

    const openRevokeDialog = (data: ApiAccessModel) => {
        setShowConfirmDialog(true)
        setSelectedRow(data)
        setText(appNote.APIACCESS_REVOKE)
        setButtonName("Revoke")
    }

    const openActivateWebhookDialog = (data: ApiAccessModel) => {
        setShowConfirmDialog(true)
        setSelectedRow(data)
        setText(appNote.APIACCESS_WEBHOOK_ENABLE)
        setButtonName("Activate")
    }

    const closeConfirmDialog = async (Confirm: boolean) => {
        if (Confirm) {
            setIsloading(true)
            if (buttonName === "Revoke") {
                await ApiAccessService.revokeApiAccess(selectedRow?.Id).then((res) => {
                    setToast(ToasterTexts.APIACCESS_REVOKE_SUCCESS, ToasterType.SUCCESS)
                    getGridData()
                }).catch((err) => {
                    setToast(ToasterTexts.SOMETHING_WENT_WRONG_ERROR, ToasterType.DANGER)
                })
            } else {
                const data = { id: selectedRow.Id, enableWebhook: true }
                await ApiAccessService.updateApiAccess(data).then((res) => {
                    setToast(ToasterTexts.APIACCESS_WEBHOOK_ENABLE, ToasterType.SUCCESS)
                    getGridData()
                }).catch((err) => {
                    setToast(ToasterTexts.SOMETHING_WENT_WRONG_ERROR, ToasterType.DANGER)
                })
            }
            setSelectedRow(undefined)
            setShowConfirmDialog(false)
            setButtonName("")
            setText("")
            setIsloading(false)
        }
        else {
            setSelectedRow(undefined)
            setShowConfirmDialog(false)
            setButtonName("")
            setText("")
        }
    }

    const openCreateAPIAccessDialog = () => {
        setCreateApiAccessDialog(true)
    }

    const closeCreateAPIAccessDialog = (isConfirm: boolean) => {
        if (isConfirm) {
            getGridData()
        }
        setCreateApiAccessDialog(false)
        setSelectedRow(undefined)
    }

    const onGridReady = (params) => {
        setGridApi(params.api)
        setColumnApi(params.columnApi)
    }

    const getGridData = () => {
        ApiAccessService.getAllApiAccess().then((res) => {
            const data = res.data?.map(mapToApiAccessModel)
            setData(data?.filter(x => x.Status === ApiAccessEnum.Active))
        }).catch((err) => {

        })
    }

    useEffect(() => {
        getGridData()
    }, [])

    return (
        <div className={`m-auto o-y-scroll h-p-100`}>
            <div className={`d-flex justify-between w-p-100  ${IsMobileDevice ? "" : "mt-2"}`}>
                <div className={`${IsMobileDevice ? " d-flex color-header-title-font box-shadow-display-background-shadow   align-center w-p-100" : "d-flex  h1-semibold color-settings-heading "}`}>
                    {IsMobileDevice && <span className={`d-flex`}>
                        <BackMobileIcon className={`c-pointer p-1 w-3 my-auto`}
                            onClick={() => navigate(AppRoutes.SETTINGS)} />
                    </span>
                    }
                    <div className='d-flex justify-between align-center w-p-100'>
                        <div className={`${IsMobileDevice ? "w-p-100" : "w-p-60"} `}>
                            <div className={`${IsMobileDevice ? "h4-semibold color-header-title-font" : "h2-semibold color-settings-heading"} mr-auto my-auto `}>API access</div>
                            {!IsMobileDevice && <div className='h5-regular'>
                                Issue credentials for the Public Send API to deliver 1-to-1 text messages from your activated numbers. Secrets are shown once at creation and are never recoverable.
                            </div>}
                        </div>
                        {!IsMobileDevice && <ButtonComponent ButtonLabel={'Create Api Key'} Variant={data?.length < 2 ? 'primary-btn' : 'disabled-btn'} Icon={PlusWhiteIcon} OnClick={() => openCreateAPIAccessDialog()} />}
                    </div>
                </div>
            </div>
            <div className='h-v-45 mt-2'>
                <GridComponent
                    ColumnDefs={ColumnDefs}
                    OnGridReady={onGridReady}
                    RowData={data}
                    IsPaginationEnabled={false}
                    EmptyLabel={emptyLabels.APIKEYS}
                    LockVisible={true}
                    isHorizontalScroll={true}
                    IsSettingsGrid
                />
            </div>
            {
                showCreateApiAccessDialog &&
                <APIAccessViewComponent
                    OnClose={(isConfirm) => { closeCreateAPIAccessDialog(isConfirm) }}
                    Show={showCreateApiAccessDialog}
                    ApiAccess={selectedRow}
                />
            }
            {
                showConfirmDialog &&
                <Confirm_Dialog
                    Show={showConfirmDialog}
                    onClose={closeConfirmDialog}
                    Text={text}
                    ButtonName={buttonName}
                    Loading={isLoading}
                />
            }
        </div>
    )
}

export default APIACCESSCOMPONENT